import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { FsRule, FsRuleTemplate, FsRuleTemplateValidOptions } from '@lib/api/entities';
import { t } from '@lib/translation/helpers/translation.helper';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class FsRulesAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getRulesList(params?: ISnListQueryParameters): Observable<ISnListResponse<FsRule>> {
    return this._httpClient.get('/api/rules/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((rule) => new FsRule(rule)),
      })),
      tap({
        error: () => this._toastrService.error(t('settings.rules.messages.retrieveError')),
      }),
    );
  }

  public getRule(id: string): Observable<FsRule> {
    return this._httpClient
      .get(`/api/rules/${id}/`)
      .pipe(map((response: any) => new FsRule(response)));
  }

  public createRule(data: any): Observable<FsRule> {
    return this._httpClient.post('/api/rules/', data).pipe(
      map((response: any) => new FsRule(response)),
      tap({
        next: () => this._toastrService.success(t('settings.rules.messages.created')),
        error: () => this._toastrService.error(t('settings.rules.messages.createError')),
      }),
    );
  }

  public updateRule(id: string, data: any): any {
    return this._httpClient.patch(`/api/rules/${id}/`, data).pipe(
      map((response: any) => new FsRule(response)),
      tap({
        next: () => this._toastrService.success(t('settings.rules.messages.updated')),
        error: () => this._toastrService.error(t('settings.rules.messages.updateError')),
      }),
    );
  }

  public deleteRule(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('settings.rules.messages.deleteConfirm'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/rules/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('settings.rules.messages.deleted')),
          error: () => this._toastrService.error(t('settings.rules.messages.deleteError')),
        }),
      );
  }

  public getRuleTemplateList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsRuleTemplate>> {
    return this._httpClient.get('/api/rule-template/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((ruleTemplate) => new FsRuleTemplate(ruleTemplate)),
      })),
      tap({
        error: () =>
          this._toastrService.error(t('settings.rules.messages.retrieveTemplatesError')),
      }),
    );
  }

  public getRuleTemplate(id: string): Observable<FsRuleTemplate> {
    return this._httpClient
      .get(`/api/rule-template/${id}/`)
      .pipe(map((response: any) => new FsRuleTemplate(response)));
  }

  public getRuleTemplateValidOptions(): Observable<FsRuleTemplateValidOptions> {
    return this._httpClient
      .get('/api/rule-template/valid_options/')
      .pipe(map((response: any) => new FsRuleTemplateValidOptions(response)));
  }
}
